<template>
  <RexDataTable
    :loading="loading"
    :headers="headersApp"
    :items="itemsApp"
    :search="true"
    :title="getTitle"
    highlight="primay"
    width="100%"
    height="20rem"
    :total="total"
    :pageNo="currentPage"
    @onPageChange="onAppPage"
    @onSearchChange="onAppSearch"
    @updateSearch="onAppSort"
    @redirectInput="redirectAppInput"
    @onClear="clearSearchApp"
    :defualtPageProps="getDefaultPageProps()"
  >
    <template #error v-if="!hasPermission">
      <PermissionDenied
        class="standard-widget-denied"
        :style="{ height: '31rem' }"
      />
    </template>
  </RexDataTable>
</template>

<script>
/**
 * Author: Tarun Rawat
 * Description: It is wrapper component for all the type overview(VM,BM,APM,DM,MM etc)  on Home Page
 * For refernce Please refer:
 *
 * src/components/Home/ApplicationOverview.vue
 * src/components/Home/BrowserOverview.vue
 * src/components/VM/VMOverview.vue
 *
 *
 *
 */
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import RexDataTable from '@/rex-components/rex-DataTable.vue';
import debounce from 'lodash/debounce';
import { SortOrder } from '@/utils/enums/sortOrder';
import { SortKeyMap } from '@/components/VM/vm.types';
import shortenTime from '@/utils/time/shortenTime.js';
import * as types from '@/store/modules/vm/vm-types';
import {
  OverViewsHeaders,
  OverviewTableName,
  getParamsByView,
  CapabilityMap,
  getSortingBy,
  getSortMetric,
  redirectTo,
  translatedOverviewTableName,
} from '@/components/Home/utils/constants';
import PermissionDenied from '@/rex-components/PermissionDenied/PermissionDenied.vue';
import { usePermissions } from '@/hooks/usePermissions';
export default {
  name: 'TabularOverviewWrapper',
  components: {
    RexDataTable,
    PermissionDenied,
  },
  props: {
    ViewName: { type: String, required: true },
    capability: { type: String, required: true },
  },
  data: function () {
    return {
      currentPage: 1,
      pageSize: 5,
      total: 0,
      keyword: '',
      metricType: getSortMetric(this.ViewName),
      sortOrder: SortOrder.DES,
      itemsApp: [],
      searchData: [],
      loading: true,
      tablesName: translatedOverviewTableName,
    };
  },
  computed: {
    ...mapGetters('globalState/settings', ['getDurationTime']),
    ...mapState('homeState', ['applicationData', 'applicationDataTotal']),
    ...mapState('homeState', ['browserData', 'browserDataTotal']),
    ...mapState('homeState', ['mobilerData', 'mobileDataTotal']),
    ...mapGetters('globalState/user', ['getTeamId']),
    headersApp() {
      return OverViewsHeaders[this.ViewName]();
    },
    getTitle() {
      return translatedOverviewTableName()[this.ViewName];
    },
    hasPermission() {
      const { hasCapabilityPermission } = usePermissions();
      return hasCapabilityPermission(this.capability);
    },
  },
  watch: {
    getDurationTime: function (newValue, oldValue) {
      this.loadService();
    },
  },
  mounted: async function () {
    this.loadService();
  },
  /**
   * Merging all the actions and state from all components
   * TODO : Optimize it more
   */
  methods: {
    /*
    /**
     * for APM monitoring
     */
    ...mapActions('apmState/apmCommonState', [
      'selectEndpoint',
      'selectService',
      'selectInstance',
    ]),
    ...mapMutations('globalState/app', ['setCurrentCapability']),
    ...mapActions('homeState', ['GET_APPLICATION_META']),
    /**
     * For VM monitoring
     */
    ...mapActions('vmState/overview', ['GET_VM_META']),
    ...mapActions('vmState/vmCommonState', [
      'SET_CURRENT_VM',
      'SET_CURRENT_VM_SERVICE',
    ]),
    ...mapMutations('vmState/vmCommonState', {
      setSubtypeMutation: types.vmMutations.SET_CURRENT_SUBTYPE,
    }),
    /**
     * For browser monitoring
     *  */

    ...mapActions('browserState/browserCommonState', ['selectBrowserService']),
    ...mapActions('homeState', ['GET_BROWSER_META']),

    /**
     * For mobile monitoring
     *  */

    ...mapActions('mobileState/mobileCommonState', ['selectMobileService']),
    ...mapActions('homeState', ['GET_MOBILE_META']),
    /**
     * For alarm
     */
    ...mapMutations('alarmState', [
      'SET_ALARM_CAPABILITY',
      'SET_CURRENT_APPLICATION',
    ]),

    /**
     * For AlertV2
     */
    ...mapMutations('alertV2State/configure', ['setCurrentAlertCapability']),
    ...mapMutations('alertV2State/configure', ['setCurrentAlertApplication']),

    /**
     * This is where API calls are made
     */
    async loadService() {
      /**
       * ACTION MAP
       * Based on current view,desired actions  will be called
       *
       */
      const apis = {
        [OverviewTableName.VM]: this.GET_VM_META,
        [OverviewTableName.BM]: this.GET_BROWSER_META,
        [OverviewTableName.APM]: this.GET_APPLICATION_META,
        [OverviewTableName.MM]: this.GET_MOBILE_META,
      };
      const params = getParamsByView(
        this,
        CapabilityMap[this.ViewName],
        this.getTeamId,
      );
      try {
        let data = await apis[this.ViewName](params);
        /**
         * Since this action make data available via diff state variable
         * so conditionally assign it to itemApp and total variable
         *
         */
        if (this.ViewName === OverviewTableName.VM) {
          this.itemsApp = data.records;
          this.total = data.total;
        }
        if (this.ViewName === OverviewTableName.BM) {
          this.itemsApp = this.browserData;
          this.total = this.browserDataTotal;
        }
        if (this.ViewName === OverviewTableName.APM) {
          this.itemsApp = this.applicationData;
          this.total = this.applicationDataTotal;
        }
        if (this.ViewName === OverviewTableName.MM) {
          this.itemsApp = this.mobilerData;
          this.itemsApp.forEach((event) => {
            if (event.respTime) {
              event.respTime = shortenTime(event.respTime);
            }
          });
          this.total = this.mobileDataTotal;
        }
      } catch (error) {
        console.log('error', error);
      } finally {
        this.loading = false;
      }
    },
    handleSearch: debounce(function () {
      this.loadService();
    }, 300),
    onAppSearch(data) {
      this.keyword = data;
      if (data.length > 2 || data.length === 0) {
        this.currentPage = 1;
        this.loadService();
        return;
      }
      this.handleSearch();
    },
    onAppPage(state) {
      this.currentPage = state.pageNo;
      this.pageSize = state.pageSize;
      this.loadService();
    },
    onAppSort(data) {
      //console.log('onEndSort', data);
      this.sortOrder = data.order;
      /**
       * Get metricApp according to view
       */

      this.metricType = getSortingBy(this.ViewName, data.header);

      /**
       * TODO
       */
      this.loadService();
    },
    redirectAppInput(option) {
      /**
       * Calling utility function
       * This will redirect and call actions based on current view
       */
      redirectTo(this, this.ViewName, option, this.getDurationTime);
    },
    clearSearchApp() {
      this.keyword = '';
      this.currentPage = 1;
      this.loadService();
    },
    getDefaultPageProps() {
      return ['5', '10', '15'];
    },
  },
};
</script>
<style lang="scss" scoped>
:deep(.header-table) {
  font-size: 10px !important;
}
:deep(.sort-buttons),
:deep(.sort-buttons-visible) {
  transform: scale(0.85);
}

.standard-widget-denied {
  height: calc(100% - 2.375rem);
}
</style>
